<template>
  <div>
    <!-- Scrim only below the breakpoint, where the sidebar floats over the page
         and tapping outside is how people expect to dismiss it. On a wide screen
         it sits alongside the content and there is nothing to dismiss. -->
    <transition name="fade">
      <div v-if="open" class="scrim" @click="close"></div>
    </transition>

    <transition name="slide">
      <nav v-if="open" class="drawer" aria-label="Main menu">

        <ul class="items">
          <li v-for="item in items" :key="item.label">
            <!-- Sections that expand rather than navigate -->
            <template v-if="item.children">
              <button
                class="item group"
                :class="{ expanded: expanded.has(item.label) }"
                @click="toggle(item.label)"
              >
                {{ item.label }}
                <span class="caret">&#8964;</span>
              </button>

              <ul v-if="expanded.has(item.label)" class="sub">
                <li v-for="child in item.children" :key="child.label">
                  <template v-if="child.children">
                    <button
                      class="item sub-item group"
                      :class="{ expanded: expanded.has(child.label) }"
                      @click="toggle(child.label)"
                    >
                      {{ child.label }}
                      <span class="caret">&#8964;</span>
                    </button>

                    <ul v-if="expanded.has(child.label)" class="sub">
                      <li v-for="leaf in child.children" :key="leaf.label">
                        <component
                          :is="leaf.route ? 'router-link' : 'span'"
                          :to="leaf.route ? { name: leaf.route } : undefined"
                          class="item leaf"
                          :class="{ pending: !leaf.route }"
                          @click="leaf.route && navigated()"
                        >{{ leaf.label }}</component>
                      </li>
                    </ul>
                  </template>

                  <component
                    v-else
                    :is="child.route ? 'router-link' : 'span'"
                    :to="child.route ? { name: child.route } : undefined"
                    class="item sub-item"
                    :class="{ pending: !child.route }"
                    @click="child.route && navigated()"
                  >{{ child.label }}</component>
                </li>
              </ul>
            </template>

            <!-- Plain entries -->
            <component
              v-else
              :is="item.route ? 'router-link' : 'span'"
              :to="item.route ? { name: item.route } : undefined"
              class="item"
              :class="{ pending: !item.route }"
              @click="item.route && navigated()"
            >{{ item.label }}</component>
          </li>
        </ul>

        <footer class="foot">
          <p class="powered">Powered by</p>
          <img class="foot-logo" src="@/assets/Epoka-white.png" alt="Epoka">
        </footer>
      </nav>
    </transition>
  </div>
</template>


<script setup>
import { ref, computed } from 'vue'
import { isAdmin } from '@/auth/authService'

defineProps({ open: Boolean })
const emit = defineEmits(['close'])

// Entries without a `route` are not built yet. They are shown but visibly
// inactive rather than omitted, so the menu reflects the intended shape of the
// product, and rather than linking somewhere that 404s.
// News and Activity are withdrawn, not merely hidden: their routes are removed
// from the router as well, so a direct link cannot reach them either. Both are
// built and working -- putting them back is restoring two entries here and two
// routes, nothing more.
// Two menus, not one filtered menu.
//
// A regular user's list is not the admin list with things missing: Dashboard,
// their own registrations, and the settings that govern their own dashboard.
// Writing them separately keeps each honest, rather than accumulating flags on
// entries that mostly do not apply.
// An admin is a user as well, so their menu is the user's plus the
// administrative section, not a different list. Building it the other way is
// what left Dashboard, Registrations and Settings off the admin menu entirely.
// An admin is a user as well, so their menu is the user's plus the
// administrative section rather than a separate list.
const USER_ITEMS = [
  { label: 'Dashboard', route: 'Display' },
  { label: 'Registrations', route: 'Registrations' },
  { label: 'Change Status', route: 'ChangeStatus' },
  { label: 'Settings', route: 'UserSettings' }
]

const ADMIN_EXTRAS = [
  { label: 'Presence', route: 'Sites' },
  { label: 'Statistics', route: 'Reports' },
  {
    label: 'Admin',
    children: [
      {
        label: 'Management',
        children: [
          { label: 'Badges', route: 'Badges' },
          { label: 'Company groups', route: 'CompanyGroups' },
          { label: 'Unmapped badges', route: 'UnmappedBadges' },
          { label: 'Departments', route: 'DepartmentManagers' },
          { label: 'Display', route: 'DisplaySettings' },
          { label: 'Doors', route: 'Doors' },
          { label: 'Manual entries', route: 'ManualPresence' },
          { label: 'Screens', route: 'Screens' },
          { label: 'Statuses', route: 'StatusTypes' },
          { label: 'Offices', route: 'Locations' }
        ]
      }
    ]
  }
]

const items = computed(() => (isAdmin() ? [...USER_ITEMS, ...ADMIN_EXTRAS] : USER_ITEMS))

// Admin and Management start open, matching the reference, so the items under
// them are visible without a hunt.
const expanded = ref(new Set(['Admin', 'Management']))

function toggle(label) {
  const next = new Set(expanded.value)
  if (next.has(label)) next.delete(label)
  else next.add(label)
  expanded.value = next
}

function close() {
  emit('close')
}

// Kept as the click handler so the markup need not change, but deliberately
// empty: following a link should not close the menu.
function navigated() {}

// Navigating deliberately does NOT close the menu. The sidebar sits beside the
// page rather than over it, and closing the thing you navigate with means
// reopening it on every click. The burger and the scrim are the only things
// that close it.
</script>


<style scoped>
.scrim {
  position: fixed;
  inset: 60px 0 0 0;
  background: rgba(15, 26, 44, 0.35);
  z-index: 90;
  display: none;
}

/* Only where the sidebar overlaps the page. */
@media (max-width: 899px) {
  .scrim { display: block; }
}

/* Starts below the header rather than at the top of the window, so the two do
   not overlap and the brand stays reachable with the menu open. */
.drawer {
  position: fixed;
  top: 60px;
  left: 0;
  bottom: 0;
  width: 260px;
  max-width: 82vw;
  background: #0e2445;
  color: #ffffff;
  z-index: 100;
  display: flex;
  flex-direction: column;
  padding: 18px 0 24px;
  overflow-y: auto;
}

/* ---------- Items ---------- */

.items {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}

.sub {
  list-style: none;
  margin: 0;
  padding: 0;
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  text-align: left;
  color: #e8eef6;
  background: none;
  border: none;
  padding: 9px 24px;
  text-decoration: none;
  cursor: pointer;
}

.item:hover { color: #ffffff; background: rgba(255, 255, 255, 0.05); }

.item.router-link-active { color: #ed6e01; }

.sub-item { padding-left: 42px; font-size: 15px; }
.leaf { padding-left: 62px; font-size: 15px; font-weight: 400; }

/* Not built yet: shown so the menu reflects the intended product, but plainly
   inert rather than a link that goes nowhere. */
.pending {
  color: #7c8ea6;
  cursor: default;
}

.pending:hover { color: #7c8ea6; background: none; }

.caret {
  font-size: 13px;
  color: #91a3ba;
  transition: transform 0.18s ease;
}

.group.expanded .caret { transform: rotate(180deg); }

/* ---------- Footer ---------- */

.foot {
  margin-top: 30px;
  padding: 0 24px;
  text-align: center;
}

.powered {
  font-size: 11px;
  color: #8fa2b8;
  margin: 0 0 7px;
}

/* The white variant, so the sage and orange survive. Inverting the dark logo
   flattened them out. */
.foot-logo {
  height: 20px;
  width: auto;
}

/* ---------- Transitions ---------- */

.slide-enter-active, .slide-leave-active { transition: transform 0.22s ease; }
.slide-enter-from, .slide-leave-to { transform: translateX(-100%); }

.fade-enter-active, .fade-leave-active { transition: opacity 0.22s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .slide-enter-active, .slide-leave-active,
  .fade-enter-active, .fade-leave-active { transition: none; }
  .caret { transition: none; }
}
</style>
